import { DEFAULTS } from '@/config/training';

/**
 * Lightweight preferences only. Anything structured — sessions, statistics —
 * belongs in IndexedDB; localStorage is synchronous and will block the main
 * thread if it is asked to hold anything real.
 */
export interface Preferences {
  onboardingComplete: boolean;
  lastBpm: number;
  lastChordIds: string[];
  lastBeatsPerChord: number;
  lastDurationMin: number;
  clickEnabled: boolean;
  adaptiveTempo: boolean;
}

const KEY = 'chord-trainer:prefs';

const FALLBACK: Preferences = {
  onboardingComplete: false,
  lastBpm: DEFAULTS.bpm,
  lastChordIds: [...DEFAULTS.starterChords],
  lastBeatsPerChord: DEFAULTS.beatsPerChord,
  lastDurationMin: DEFAULTS.durationMin,
  clickEnabled: true,
  adaptiveTempo: true,
};

export function loadPreferences(): Preferences {
  if (typeof localStorage === 'undefined') return { ...FALLBACK };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...FALLBACK };
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return { ...FALLBACK, ...parsed };
  } catch {
    // Corrupt or blocked storage (private mode, disabled cookies) must never
    // stop the app from opening.
    return { ...FALLBACK };
  }
}

export function savePreferences(patch: Partial<Preferences>): Preferences {
  const next = { ...loadPreferences(), ...patch };
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // Quota or private mode. Preferences are a convenience, not data.
    }
  }
  return next;
}
